import { Button, Card, CardContent, CardHeader, CardTitle, LoadingSpinner, Pagination, QueryErrorAlert } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import type { NotesPage } from '../types/agreementTimeline.types';
import { canAddNote } from '../utils/timelineEntryLabels';
import { AgreementNoteItem } from './AgreementNoteItem';

interface Props {
    isLoading: boolean;
    isError: boolean;
    notes: NotesPage | undefined;
    currentPage: number;
    status: string;
    onRetry: () => void;
    onPageChange: (page: number) => void;
    onAddNote: () => void;
}

const headingId = 'agreement-notes-heading';

export function AgreementNotesTab({ isLoading, isError, notes, currentPage, status, onRetry, onPageChange, onAddNote }: Props) {
    return (
        <Card>
            <CardHeader className="flex items-center justify-between gap-4">
                <CardTitle>
                    <span id={headingId}>Notes</span>
                </CardTitle>
                {canAddNote(status) && (
                    <PermissionGuard permission={{ resource: 'agreement', action: 'note.add' }}>
                        <Button type="button" size="sm" variant="outline" onClick={onAddNote}>
                            Add note
                        </Button>
                    </PermissionGuard>
                )}
            </CardHeader>
            <CardContent>
                <section aria-labelledby={headingId}>
                    <NotesBody isLoading={isLoading} isError={isError} notes={notes} onRetry={onRetry} />
                    {notes && notes.meta.lastPage > 1 && (
                        <div className="mt-4">
                            <Pagination
                                currentPage={currentPage}
                                lastPage={notes.meta.lastPage}
                                total={notes.meta.total}
                                perPage={notes.meta.perPage}
                                onPageChange={onPageChange}
                            />
                        </div>
                    )}
                </section>
            </CardContent>
        </Card>
    );
}

function NotesBody({ isLoading, isError, notes, onRetry }: Pick<Props, 'isLoading' | 'isError' | 'notes' | 'onRetry'>) {
    if (isLoading) {
        return <LoadingSpinner message="Loading notes" />;
    }

    if (isError || !notes) {
        return (
            <QueryErrorAlert message="Failed to load the agreement's notes." onRetry={onRetry} />
        );
    }

    if (notes.entries.length === 0) {
        return <p className="text-sm text-mp-muted">No notes have been recorded on this agreement yet.</p>;
    }

    return (
        <ol aria-label="Agreement notes" className="space-y-3">
            {notes.entries.map((entry) => (
                <AgreementNoteItem key={entry.id} entry={entry} />
            ))}
        </ol>
    );
}
